/** biome-ignore-all lint/a11y/noNoninteractiveTabindex: Carousel root needs tabindex for APG keyboard navigation (Arrow/Home/End/PageUp/PageDown). */
import "./SlideShow.css";
import {
	ThumbnailTrack,
	ThumbnailTrackCaption,
	type ThumbnailTrackHandle,
	type ThumbnailTrackItem,
} from "@ui/molecules/ThumbnailTrack/ThumbnailTrack.jsx";
import { Stack } from "@ui/Stack.jsx";
import { createMemo, createSignal, Show } from "solid-js";

export type SlideShowSlide = {
	url: string;
	summary?: {
		headline?: string;
		summary?: string;
	};
};

type Props = {
	slides: SlideShowSlide[];
	title?: string;
};

/**
 * Deck-style carousel: main image, headline, numbered thumbnails, arrow buttons (hold-to-repeat),
 * swipe on the hero image, and keyboard shortcuts when focused.
 *
 * Usage: `import { SlideShow } from "@ui/molecules/SlideShow/SlideShow"` then pass
 * `slides` as `{ url, summary?: { headline?, summary? } }[]`. See Design System → SlideShow → Usage example.
 *
 * The thumbnail strip is {@link ThumbnailTrack}, shared with other paged previews;
 * this component adds the hero image and caption above it.
 *
 * Accessibility: labeled `<section>` with carousel roledescription and focus for arrow keys (APG),
 * polite live announcements on slide change, labeled prev/next buttons, thumbnail `<button>`s with
 * `aria-current`, roving `tabIndex` on thumbnails (Tab targets the active slide), active-thumb ring on image only.
 */
export const SlideShow = (props: Props) => (
	<Show when={props.slides.length > 0}>
		<SlideShowTrack slides={props.slides} title={props.title} />
	</Show>
);

/** Renders only when {@link SlideShow}'s caller passes at least one slide. */
function SlideShowTrack(props: { slides: SlideShowSlide[]; title?: string }) {
	const [selectedIndex, setSelectedIndex] = createSignal(0);
	let trackHandle: ThumbnailTrackHandle | undefined;

	const slideTitle = (idx: number) =>
		props.slides[idx]?.summary?.headline?.trim() ?? props.title ?? "";

	const thumbnailButtonLabel = (idx: number) => {
		const total = props.slides.length;
		const title = slideTitle(idx);
		const pos = `${idx + 1} of ${total}`;
		return title.length > 0
			? `Slide ${pos}, ${title}. Select to show this slide in the viewer above.`
			: `Slide ${pos}. Select to show this slide in the viewer above.`;
	};

	const thumbnails = createMemo<ThumbnailTrackItem[]>(() =>
		props.slides.map((slide, idx) => ({
			url: slide.url,
			ariaLabel: thumbnailButtonLabel(idx),
		})),
	);

	const primaryImageAlt = createMemo(() => {
		const i = selectedIndex();
		const title = slideTitle(i);
		return title.length > 0
			? title
			: `Slide ${i + 1} of ${props.slides.length}`;
	});

	const liveSlideAnnouncement = createMemo(() => {
		const i = selectedIndex();
		const title = slideTitle(i);
		const pos = `${i + 1} of ${props.slides.length}`;
		return title.length > 0 ? `Slide ${pos}, ${title}` : `Slide ${pos}`;
	});

	const selectSlide = (index: number) => {
		const clamped = Math.max(0, Math.min(index, props.slides.length - 1));
		setSelectedIndex(clamped);
		trackHandle?.scrollToIndex(clamped);
	};

	let touchStartX = 0;
	let touchStartY = 0;

	const onTouchStart = (e: TouchEvent) => {
		touchStartX = e.touches[0].clientX;
		touchStartY = e.touches[0].clientY;
	};

	const onTouchEnd = (e: TouchEvent) => {
		const dx = e.changedTouches[0].clientX - touchStartX;
		const dy = e.changedTouches[0].clientY - touchStartY;
		if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
			selectSlide(selectedIndex() + (dx < 0 ? 1 : -1));
		}
	};

	return (
		<section
			class="gvtui-slideshow-wrapper"
			aria-roledescription="carousel"
			aria-label="Slide presentation"
			tabIndex={0}
			onKeyDown={(e) => trackHandle?.onKeyDown(e)}
		>
			<Stack class="gvtui-slideshow" direction="column" spacing="large">
				<img
					class="selected-slide"
					src={props.slides[selectedIndex()].url}
					alt={primaryImageAlt()}
					onTouchStart={onTouchStart}
					onTouchEnd={onTouchEnd}
				/>
				<ThumbnailTrackCaption
					title={props.slides[selectedIndex()].summary?.headline}
					selectedIndex={selectedIndex()}
					total={props.slides.length}
				/>
				<ThumbnailTrack
					items={thumbnails()}
					selectedIndex={selectedIndex()}
					onSelect={setSelectedIndex}
					itemLabel="Slide"
					announcement={liveSlideAnnouncement()}
					handle={(handle) => {
						trackHandle = handle;
					}}
				/>
			</Stack>
		</section>
	);
}
